import { ACTOR_BUYER, ACTOR_PAYMENT, ACTOR_SYSTEM, ORDER_STATUS_LABEL } from './constants.js'

const BAHT = new Intl.NumberFormat('th-TH', { maximumFractionDigits: 0 })

/** One money formatter for buttons, tables, e-mails and exports. */
export const baht = (amount: number): string => `฿${BAHT.format(amount)}`

const BAHT_EXACT = new Intl.NumberFormat('th-TH', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * Two decimal places, for figures that only make sense exactly.
 *
 * Every price in this system is whole baht, so `baht` drops decimals and is right
 * almost everywhere. The exception is a tax split: ฿282 at 7% is ฿263.55 + ฿18.45,
 * and rounding those to ฿264 + ฿18 shows a buyer two numbers that do not add up to
 * the one they are about to pay.
 */
export const bahtExact = (amount: number): string => `฿${BAHT_EXACT.format(amount)}`

/** `+40` on a size button; empty when the size costs nothing extra. */
export const extraLabel = (extra: number): string => (extra > 0 ? `+${BAHT.format(extra)}` : '')

/**
 * Enough for the buyer to recognise their own inbox, not enough for a stranger
 * holding the order id to learn the address.
 */
export const maskEmail = (email: string): string => {
  const at = email.lastIndexOf('@')
  if (at <= 0) return '•••'
  const user = email.slice(0, at)
  const domain = email.slice(at)
  const head = user.slice(0, Math.min(2, user.length))
  return `${head}${'•'.repeat(Math.max(3, user.length - head.length))}${domain}`
}

export const maskPhone = (phone: string): string =>
  phone.length < 6 ? '•••' : `${phone.slice(0, 3)}•••${phone.slice(-2)}`

const DATE_TIME = new Intl.DateTimeFormat('th-TH', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Asia/Bangkok',
})
const DATE_ONLY = new Intl.DateTimeFormat('th-TH', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'Asia/Bangkok',
})

export const thaiDateTime = (date: Date): string => DATE_TIME.format(date)
export const thaiDate = (date: Date): string => DATE_ONLY.format(date)

export type OneLineAddress = {
  recipient: string
  addressLine: string
  subdistrict: string
  district: string
  province: string
  postcode: string
}

/** Used by the admin copy button, the shipping export and the paid e-mail alike. */
export const addressOneLine = (address: OneLineAddress): string =>
  [
    address.recipient,
    address.addressLine,
    `ต.${address.subdistrict}`,
    `อ.${address.district}`,
    `จ.${address.province}`,
    address.postcode,
  ].join(' ')

export type LabelledLine = {
  name: string
  colorName?: string | undefined
  size: string
}

/** `เสื้อแขนสั้น น้ำเงิน M ×2` — the one phrasing for a line item, everywhere. */
export const lineLabel = (line: LabelledLine & { qty: number }): string =>
  `${[line.name, line.colorName, line.size].filter(Boolean).join(' ')} ×${line.qty}`

/** `น้ำเงิน / M` — the variant on its own, for table cells and swap pickers. */
export const variantLabel = (line: Pick<LabelledLine, 'colorName' | 'size'>): string =>
  line.colorName ? `${line.colorName} / ${line.size}` : line.size

/**
 * History rows store raw values, because the log is data and should not be written
 * in one language. `from`/`to` is sometimes a status code and sometimes a size, so
 * translation happens here at render time.
 */
export const historyValue = (value: string): string =>
  (ORDER_STATUS_LABEL as Record<string, string | undefined>)[value] ?? value

/**
 * Who did it, in words.
 *
 * The log stores `system` / `payment` / `buyer` as tokens and an e-mail address for
 * anyone else, because it is data. Rendered raw, "buyer" next to a cancellation
 * reads as an accusation the reader has to decode — and an admin who cancelled a
 * different order will reasonably think the system got it wrong.
 */
export const historyActor = (actor: string): string =>
  ({
    [ACTOR_SYSTEM]: 'ระบบอัตโนมัติ',
    [ACTOR_PAYMENT]: 'ระบบชำระเงิน',
    [ACTOR_BUYER]: 'ผู้สั่งซื้อ (ทำเองจากหน้าเว็บ)',
  })[actor] ?? actor
